import {
    SendLogEmailPreview,
    SendLogPage,
} from "components/KBo/Models/SendLog";
import BaseService from "./BaseService";

/** Matches the backend's `Data.Models.Emailing.EmailSendSourceType` enum. */
export type EmailSendSourceType =
    | "Other"
    | "KboFlowRun"
    | "SubscribeToSearch"
    | "SignalsDashboard";

export interface GetSendLogQuery {
    sourceType: EmailSendSourceType;
    recipient?: string;
    startDate?: string;
    endDate?: string;
    sortField?: string;
    sortDirection?: "asc" | "desc";
    page: number;
    pageSize: number;
    abortSignal?: AbortSignal;
}

export interface CreateSendLogChatResponse {
    chatId: string;
}

/**
 * Reads the generic `EmailSends` audit log (see `EmailSendsController`). Only the KBo Settings
 * Send Log page consumes this today, scoped to `KboFlowRun` sends, but the endpoint itself
 * covers every `EmailSendSourceType`.
 */
export class EmailSendsService extends BaseService {
    protected urlBase = "api/EmailSends";

    public async getSendLog({
        sourceType,
        recipient,
        startDate,
        endDate,
        sortField,
        sortDirection,
        page,
        pageSize,
        abortSignal,
    }: GetSendLogQuery): Promise<SendLogPage> {
        const params = new URLSearchParams({
            sourceType,
            page: String(page),
            pageSize: String(pageSize),
        });
        if (recipient) params.set("recipient", recipient);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (sortField) params.set("sortField", sortField);
        if (sortDirection) params.set("sortDirection", sortDirection);

        return this.get<SendLogPage>(params, "SendLog", abortSignal);
    }

    /** Loads the persisted content for a selected send-log row on demand. */
    public async getSendLogPreview(
        sendCorrelationId: string,
        sourceType: EmailSendSourceType,
        abortSignal?: AbortSignal,
    ): Promise<SendLogEmailPreview> {
        return this.get<SendLogEmailPreview>(
            new URLSearchParams({ sourceType }),
            `SendLog/${sendCorrelationId}`,
            abortSignal,
        );
    }

    /** Creates a fresh current-user KBo Flow chat from the selected sent report. */
    public async createSendLogChat(
        sendCorrelationId: string,
    ): Promise<CreateSendLogChatResponse> {
        return this.post<CreateSendLogChatResponse>(
            null,
            new URLSearchParams(),
            `SendLog/${sendCorrelationId}/Chat`,
        );
    }
}

export default EmailSendsService;
